import {
  Content as TabsContentPrimitive,
  List as TabsListPrimitive,
  Root as TabsRoot,
  Trigger as TabsTriggerPrimitive,
} from '@radix-ui/react-tabs';
import React, {
  Children,
  cloneElement,
  createContext,
  forwardRef,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
  type ElementRef,
  type ReactNode,
} from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './dropdown-menu';
import { Button } from './button';

import { cn } from '@parentsquare/squarekit/lib/utils';

type TabsVersion = 'v0' | 'v1';

// Shared by every part of the Tabs subtree so consumers only set `version` on `<Tabs>`.
const TabsVersionContext = createContext<TabsVersion>('v1');

type TabsRootProps = ComponentPropsWithoutRef<typeof TabsRoot> & {
  version?: TabsVersion;
};

const Tabs = forwardRef<ElementRef<typeof TabsRoot>, TabsRootProps>(
  ({ version = 'v1', children, ...props }, ref) => (
    <TabsVersionContext.Provider value={version}>
      <TabsRoot ref={ref} {...props}>
        {children}
      </TabsRoot>
    </TabsVersionContext.Provider>
  )
);
Tabs.displayName = 'Tabs';

const tabsListV0Classic =
  'tw:flex tw:h-auto tw:min-h-[44px] tw:flex-wrap tw:items-center tw:justify-start tw:pl-2.5';

const tabsListV1 = 'tw:flex tw:h-9 tw:items-center tw:gap-(--spacing-2) tw:ml-[7.5px] tw:pl-2.5';

const tabsListVariants = cva('', {
  variants: {
    version: { v0: tabsListV0Classic, v1: tabsListV1 },
    // variant kept for API compatibility with v0 callers; no longer affects styling.
    variant: { classic: '' },
  },
  defaultVariants: {
    version: 'v1',
  },
});

const tabsTriggerV0Classic = `
    tw:rounded-sm tw:px-[15px] tw:py-2.5 tw:aria-selected:relative tw:aria-selected:z-20
    tw:aria-selected:rounded-b-none tw:aria-selected:border tw:aria-selected:border-b-0
    tw:aria-selected:border-(--color-border-dark) tw:data-[state=active]:shadow-none
    tw:data-[state=active]:bg-background tw:data-[state=active]:text-[#555555]
    tw:data-[state=active]:mb-[-2px] tw:data-[state=active]:pb-[12px]
    tw:text-sm tw:mr-[2px] tw:hover:bg-[#EEEEEE] tw:hover:rounded-b-none
    tw:data-[state=inactive]:text-primary tw:whitespace-nowrap
  `;

// v1 trigger: 36px tall tab that "punches through" the strip's bottom border when active.
// The -mb-px on active pulls the tab down 1px so its bottom edge covers the strip border.
// Focus ring uses --shadow-state-focus and lifts the tab above siblings via z-index.
const tabsTriggerV1 = [
  'tw:inline-flex tw:h-9 tw:items-center tw:gap-(--spacing-4)',
  'tw:cursor-pointer tw:px-(--spacing-12) tw:text-sm tw:font-normal tw:whitespace-nowrap',
  'tw:rounded-t-(--radius-4) tw:border tw:border-transparent tw:border-b-0',
  'tw:text-(--color-text-link-default)',
  'tw:focus-visible:outline-hidden tw:focus-visible:shadow-(--shadow-state-focus)',
  'tw:focus-visible:relative tw:focus-visible:z-10',
  'tw:hover:bg-(--color-surface-menuItem-hover)',
  'tw:data-[state=active]:relative tw:data-[state=active]:z-20 tw:data-[state=active]:-mb-px',
  'tw:data-[state=active]:cursor-default',
  'tw:data-[state=active]:bg-(--color-surface-base)',
  'tw:data-[state=active]:border-(--color-border-dark)',
  'tw:data-[state=active]:text-(--color-text-on-surface-primary)',
  'tw:data-[state=active]:hover:bg-(--color-surface-base)',
  'tw:data-[state=active]:[box-shadow:0_1px_0_0_var(--color-surface-base)]',
  // Active + focused: combine border-eraser and focus ring so neither wins alone.
  // data-[state=active]:focus-visible has higher specificity than either individually.
  'tw:data-[state=active]:focus-visible:[box-shadow:0_1px_0_0_var(--color-surface-base),var(--shadow-state-focus)]',
  'tw:disabled:cursor-not-allowed',
  'tw:disabled:text-(--color-text-on-surface-disabled)',
  'tw:disabled:hover:bg-transparent',
].join(' ');

const tabsTriggerVariants = cva('', {
  variants: {
    version: { v0: tabsTriggerV0Classic, v1: tabsTriggerV1 },
    variant: { classic: '' },
  },
  defaultVariants: {
    version: 'v1',
  },
});

interface TabItem {
  value: string;
  children: ReactNode;
  icon?: IconDefinition;
  className?: string;
  asChild?: boolean;
  disabled?: boolean;
}

interface TabsListWithOverflowProps
  extends
    ComponentPropsWithoutRef<typeof TabsListPrimitive>,
    VariantProps<typeof tabsListVariants> {
  children: ReactNode;
  onValueChange?: (value: string) => void;
}

type TabsTriggerProps = ComponentPropsWithoutRef<typeof TabsTriggerPrimitive> &
  VariantProps<typeof tabsTriggerVariants> & {
    icon?: IconDefinition;
  };

const TabsTrigger = forwardRef<ElementRef<typeof TabsTriggerPrimitive>, TabsTriggerProps>(
  ({ className, version, variant, icon, children, asChild, value, ...props }, ref) => {
    const ctxVersion = useContext(TabsVersionContext);
    const resolvedVersion = version ?? ctxVersion;
    return (
      <TabsTriggerPrimitive
        ref={ref}
        value={value}
        data-value={value}
        className={cn(tabsTriggerVariants({ version: resolvedVersion, variant }), className)}
        asChild={asChild}
        {...props}
      >
        {asChild ? (
          children
        ) : (
          <>
            {icon && (
              <FontAwesomeIcon
                icon={icon}
                aria-hidden="true"
                className={resolvedVersion === 'v1' ? '' : 'tw:mr-[5px]'}
              />
            )}
            {children}
          </>
        )}
      </TabsTriggerPrimitive>
    );
  }
);
TabsTrigger.displayName = TabsTriggerPrimitive.displayName;

const LAYOUT_SETTLE_DELAY_MS = 10;
const OVERFLOW_BUTTON_ESTIMATE_V1_PX = 100;
const OVERFLOW_BUTTON_ESTIMATE_V0_PX = 60;
const MIN_WIDTH_PADDING_PX = 20;

const TabsListWithOverflow = forwardRef<
  ElementRef<typeof TabsListPrimitive>,
  TabsListWithOverflowProps
>(({ className, version, variant, children, onValueChange, ...props }, ref) => {
  const ctxVersion = useContext(TabsVersionContext);
  const resolvedVersion = version ?? ctxVersion;
  const listRef = useRef<HTMLDivElement>(null);
  const overflowButtonRef = useRef<HTMLButtonElement>(null);
  // Set synchronously in handleTabSelect, cleared synchronously in onCloseAutoFocus.
  // Using a ref (not state) avoids the timing race where the pendingFocusValue state
  // is already cleared by the focus effect before Radix fires onCloseAutoFocus.
  const selectionMadeRef = useRef(false);

  // Extract tab information from children first so it can seed the initial state,
  // preventing a blank flash on mount and during HMR reloads.
  const tabItems = useMemo(() => {
    const items: TabItem[] = [];
    Children.forEach(children, (child) => {
      // Compare by displayName string rather than function reference so this
      // survives HMR — hot-reload creates a new TabsTrigger reference but the
      // displayName string stays stable across module re-evaluations.
      if (
        isValidElement<TabsTriggerProps>(child) &&
        (child.type as { displayName?: string })?.displayName === TabsTrigger.displayName
      ) {
        const { value, children: tabChildren, icon, className, asChild, disabled } = child.props;
        items.push({
          value,
          children: tabChildren,
          icon,
          className,
          asChild,
          disabled,
        });
      }
    });
    return items;
  }, [children]);

  // Initialize visibleTabs to all tabs so the first render is never blank.
  // calculateOverflow (below) will trim this to the correct visible subset.
  const [visibleTabs, setVisibleTabs] = useState<TabItem[]>(() => tabItems);
  const [overflowTabs, setOverflowTabs] = useState<TabItem[]>([]);
  const [minWidthForActiveTab, setMinWidthForActiveTab] = useState<number>(0);

  // After a tab selected from the overflow dropdown becomes visible, focus it.
  // We defer focus until after React has committed the tab as visible so the
  // trigger has its natural tabIndex (managed by Radix's roving tabindex) rather
  // than our hidden-state tabIndex={-1} / aria-hidden="true" overrides. Without
  // this, Radix's roving tabindex can be left in a stale state and Shift+Tab /
  // arrow-key navigation breaks.
  const [pendingFocusValue, setPendingFocusValue] = useState<string | null>(null);
  useEffect(() => {
    if (!pendingFocusValue) return;
    if (!visibleTabs.some((t) => t.value === pendingFocusValue)) return;

    const tabsRoot = listRef.current?.closest('[data-orientation]');
    const trigger = tabsRoot?.querySelector(
      `[role="tab"][data-value="${CSS.escape(pendingFocusValue)}"]`
    ) as HTMLElement | null;

    if (trigger) {
      trigger.focus();
    }
    setPendingFocusValue(null);
  }, [visibleTabs, pendingFocusValue]);
  // Custom keyboard navigation so arrow keys skip over off-screen hidden tabs.
  // Radix's roving tabindex includes every [role="tab"] in DOM order, meaning
  // a user might have to press arrow multiple times to jump from one visible tab
  // to the next when there are overflow tabs sitting between them in the DOM.
  // We intercept in the capture phase (before Radix sees the event) and restrict
  // the cycle to only the visible (non-aria-hidden) triggers.
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
      const focused = document.activeElement;
      if (!focused || focused.getAttribute('role') !== 'tab') return;

      const visibleTriggers = Array.from(
        el.querySelectorAll<HTMLElement>(
          '[role="tab"]:not([aria-hidden="true"]):not([data-disabled])'
        )
      );
      const currentIndex = visibleTriggers.indexOf(focused as HTMLElement);
      if (currentIndex === -1) return;

      let nextIndex: number;
      if (e.key === 'Home') {
        nextIndex = 0;
      } else if (e.key === 'End') {
        nextIndex = visibleTriggers.length - 1;
      } else if (e.key === 'ArrowRight') {
        nextIndex = (currentIndex + 1) % visibleTriggers.length;
      } else {
        nextIndex = (currentIndex - 1 + visibleTriggers.length) % visibleTriggers.length;
      }

      // Always suppress the event once focus is on a visible tab — even when
      // wrapping resolves to the same tab (e.g. exactly 1 visible tab where
      // nextIndex === currentIndex). Without this, the unhandled event reaches
      // Radix's roving-tabindex handler which navigates DOM-order to a hidden
      // off-screen overflow tab, silently dropping keyboard focus.
      e.stopPropagation();
      e.preventDefault();
      const next = visibleTriggers[nextIndex];
      if (next && next !== focused) {
        next.focus();
      }
    };

    el.addEventListener('keydown', handleKeyDown, true);
    return () => el.removeEventListener('keydown', handleKeyDown, true);
  }, []);

  // Calculate overflow on mount and resize
  useEffect(() => {
    const calculateOverflow = () => {
      if (!listRef.current) {
        setVisibleTabs(tabItems);
        setOverflowTabs([]);
        return;
      }

      // Get the most reliable container width measurement
      // Use clientWidth for more stable measurements
      let containerWidth = 0;

      // First try: outer container clientWidth (more stable than getBoundingClientRect)
      const outerContainer = listRef.current.parentElement;
      if (outerContainer) {
        containerWidth = outerContainer.clientWidth;
      }

      // Fallback: use the list element itself
      if (containerWidth <= 0) {
        containerWidth = listRef.current.clientWidth;
      }

      // Another fallback: try getBoundingClientRect if clientWidth fails
      if (containerWidth <= 0) {
        if (outerContainer) {
          const rect = outerContainer.getBoundingClientRect();
          containerWidth = rect?.width || 0;
        } else {
          const rect = listRef.current.getBoundingClientRect();
          containerWidth = rect?.width || 0;
        }
      }

      // In test environments, getBoundingClientRect may return 0
      // In that case, show all tabs to avoid breaking tests
      if (containerWidth <= 0) {
        setVisibleTabs(tabItems);
        setOverflowTabs([]);
        return;
      }

      // Find the currently active tab by checking DOM for aria-selected="true".
      // Use data-value (stamped on every trigger at line ~148) rather than
      // parsing aria-controls, which relies on Radix's undocumented ID format.
      const tabsRoot = listRef.current?.closest('[data-orientation]');
      let activeTabValue: string | null = null;
      if (tabsRoot) {
        const activeTabElement = tabsRoot.querySelector('[role="tab"][aria-selected="true"]');
        if (activeTabElement) {
          activeTabValue = activeTabElement.getAttribute('data-value');
        }
      }

      // Measure the actual overflow button width from the DOM when available.
      // Falls back to a version-appropriate estimate for the first render (before
      // the button exists) or when the button is not yet mounted.
      const measuredButtonWidth = overflowButtonRef.current
        ? overflowButtonRef.current.getBoundingClientRect().width
        : 0;
      const dropdownButtonWidth =
        measuredButtonWidth > 0
          ? measuredButtonWidth
          : resolvedVersion === 'v1'
            ? OVERFLOW_BUTTON_ESTIMATE_V1_PX
            : OVERFLOW_BUTTON_ESTIMATE_V0_PX;
      // Safety buffer absorbs subpixel rounding and font-rendering variance.
      const safetyBuffer = 8;
      let visibleCount = 0;

      // Create temporary elements to measure width
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.visibility = 'hidden';
      tempContainer.style.whiteSpace = 'nowrap';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '-9999px';
      tempContainer.className = cn(tabsListVariants({ version: resolvedVersion, variant }));
      document.body.appendChild(tempContainer);

      try {
        // Don't reorder tabs - keep them in original order
        // But prioritize showing the active tab by ensuring it fits
        const tabWidths: number[] = [];
        let activeTabIndex = -1;

        // First pass: measure all tabs and find active tab index
        for (let i = 0; i < tabItems.length; i++) {
          const tabItem = tabItems[i];
          if (!tabItem) continue;

          if (tabItem.value === activeTabValue) {
            activeTabIndex = i;
          }

          const tempTab = tabItem.asChild
            ? document.createElement('a')
            : document.createElement('button');
          tempTab.className = cn(
            tabsTriggerVariants({ version: resolvedVersion, variant }),
            tabItem.className
          );

          // Create a more accurate representation of the tab content
          let hasIconInChildren = false;
          let textContent = '';

          // Function to extract text from React elements recursively
          const extractTextFromReactNode = (node: ReactNode): string => {
            if (typeof node === 'string') {
              return node;
            }
            if (typeof node === 'number') {
              return String(node);
            }
            if (Array.isArray(node)) {
              return node.map(extractTextFromReactNode).join(' ');
            }
            if (isValidElement<{ children?: ReactNode }>(node)) {
              // Check if it's an icon component (like FontAwesome)
              if (node.type && typeof node.type === 'object' && 'displayName' in node.type) {
                const displayName = (node.type as { displayName?: string }).displayName || '';
                if (displayName.includes('FontAwesome') || displayName.includes('Icon')) {
                  hasIconInChildren = true;
                  return ''; // Don't include icon in text content
                }
              }
              // Extract text from children
              if (node.props && node.props.children) {
                return extractTextFromReactNode(node.props.children);
              }
            }
            return '';
          };

          // Check if there's an icon in the children and extract text
          if (tabItem.icon) {
            // Icon prop is provided
            hasIconInChildren = true;
            textContent = extractTextFromReactNode(tabItem.children);
          } else {
            // Check children for icons and extract text
            textContent = extractTextFromReactNode(tabItem.children);
          }

          if (hasIconInChildren) {
            // Create icon element that matches icon space
            const iconSpan = document.createElement('span');
            iconSpan.className = `${resolvedVersion === 'v0' ? 'tw:mr-[5px] ' : ''}tw:w-4 tw:h-4 tw:inline-block`;
            iconSpan.textContent = '●'; // Placeholder character similar to icon width
            tempTab.appendChild(iconSpan);
          }

          // Extract text content more accurately
          const textSpan = document.createElement('span');
          textSpan.textContent = textContent;
          tempTab.appendChild(textSpan);

          tempTab.style.position = 'relative';
          tempTab.style.display = 'inline-block';
          tempTab.style.visibility = 'hidden'; // More accurate than off-screen positioning
          tempContainer.appendChild(tempTab);

          const rect = tempTab.getBoundingClientRect();
          const tabWidth = (rect?.width || 0) + (resolvedVersion === 'v0' ? 2 : 0);
          tabWidths.push(tabWidth);
        }

        // Per-tab gap (CSS `gap` on the flex list) and one-time list left padding.
        // These are NOT captured by getBoundingClientRect() on individual tabs, so
        // they must be added explicitly to get accurate overflow boundaries.
        // Read from TabsListPrimitive (role="tablist"), not the outer wrapper div
        // that listRef points to — the gap and padding live on the inner element.
        const tabsListEl =
          (listRef.current?.querySelector('[role="tablist"]') as HTMLElement | null) ??
          listRef.current;
        const listStyle = getComputedStyle(tabsListEl);
        const tabGap = parseFloat(listStyle.gap) || 0;
        const listPaddingLeft = parseFloat(listStyle.paddingLeft) || 0;
        // marginLeft is also consumed space in the outer flex row (e.g. the
        // ml-[7.5px] applied to TabsListPrimitive in v1 to offset the border).
        // Not accounting for it would overestimate available space by that amount,
        // leaving the safetyBuffer as the only protection against subpixel overflow.
        const listMarginLeft = parseFloat(listStyle.marginLeft) || 0;

        // Second pass: calculate how many tabs can fit.
        // listWidth tracks the full projected width of TabsListPrimitive (margin +
        // padding + tab widths + inter-tab gaps). The overflow button sits outside
        // TabsListPrimitive in the outer flex row (no extra gap between them).
        let listWidth = listMarginLeft + listPaddingLeft;
        visibleCount = 0;

        for (let i = 0; i < tabWidths.length; i++) {
          const tabWidth = tabWidths[i] || 0;
          const gapBefore = visibleCount > 0 ? tabGap : 0;
          const wouldBeLastTab = i === tabWidths.length - 1;

          if (wouldBeLastTab) {
            if (listWidth + gapBefore + tabWidth <= containerWidth) {
              visibleCount = tabWidths.length; // All tabs fit without overflow button
              break;
            } else {
              break;
            }
          } else {
            if (
              listWidth + gapBefore + tabWidth + dropdownButtonWidth + safetyBuffer <=
              containerWidth
            ) {
              listWidth += gapBefore + tabWidth;
              visibleCount++;
            } else {
              break;
            }
          }
        }

        // Ensure at least one tab is always visible
        if (visibleCount === 0) {
          visibleCount = 1;
        }

        // Calculate minimum width needed for active tab + caret in small viewports
        let calculatedMinWidth = 0;
        if (activeTabIndex >= 0 && visibleCount < tabItems.length) {
          const activeTabWidth = tabWidths[activeTabIndex] || 0;
          calculatedMinWidth = activeTabWidth + dropdownButtonWidth + MIN_WIDTH_PADDING_PX;
        }
        setMinWidthForActiveTab(calculatedMinWidth);

        // Determine which tabs are visible vs overflow
        // Always ensure active tab is visible, but otherwise show tabs in order
        let finalVisibleTabs: TabItem[];
        let finalOverflowTabs: TabItem[];

        if (activeTabIndex >= 0 && activeTabIndex >= visibleCount) {
          // Active tab is beyond normal visible range - prioritize it.
          // Re-calculate how many prefix tabs can share the row with the active
          // tab AND the overflow button. Using the pre-calculated `visibleCount`
          // here is wrong because that count was computed against any tab width,
          // not the active tab's specific (possibly wider) width.
          const activeTab = tabItems[activeTabIndex];
          if (activeTab) {
            const activeTabWidth = tabWidths[activeTabIndex] ?? 0;
            let prefixCount = 0;
            // Start with margin + padding; gaps are added per-tab as we go.
            let prefixListWidth = listMarginLeft + listPaddingLeft;

            for (let i = 0; i < activeTabIndex; i++) {
              const w = tabWidths[i] ?? 0;
              const gapBeforeThis = prefixCount > 0 ? tabGap : 0;
              // Gap between the last prefix tab (or padding) and the active tab.
              const gapBeforeActive = tabGap;
              if (
                prefixListWidth +
                  gapBeforeThis +
                  w +
                  gapBeforeActive +
                  activeTabWidth +
                  dropdownButtonWidth +
                  safetyBuffer <=
                containerWidth
              ) {
                prefixListWidth += gapBeforeThis + w;
                prefixCount++;
              } else {
                break;
              }
            }

            const firstTabs = tabItems.slice(0, prefixCount);
            finalVisibleTabs = [...firstTabs, activeTab];

            const shownValues = new Set([...firstTabs.map((t) => t.value), activeTab.value]);
            finalOverflowTabs = tabItems.filter((tab) => !shownValues.has(tab.value));
          } else {
            // Fallback to normal order
            finalVisibleTabs = tabItems.slice(0, visibleCount);
            finalOverflowTabs = tabItems.slice(visibleCount);
          }
        } else {
          // Normal case - show tabs in order (active tab is within visible range)
          finalVisibleTabs = tabItems.slice(0, visibleCount);
          finalOverflowTabs = tabItems.slice(visibleCount);
        }

        // Batch update both states together
        setVisibleTabs(finalVisibleTabs);
        setOverflowTabs(finalOverflowTabs);
      } finally {
        if (document.body.contains(tempContainer)) {
          document.body.removeChild(tempContainer);
        }
      }
    };

    // Add a small delay to ensure DOM is ready and layout is stable
    const timeoutId = setTimeout(calculateOverflow, LAYOUT_SETTLE_DELAY_MS);

    const resizeObserver = new ResizeObserver(() => {
      setTimeout(calculateOverflow, LAYOUT_SETTLE_DELAY_MS);
    });

    if (listRef.current) {
      resizeObserver.observe(listRef.current);
    }

    // Also recalculate when DOM mutations occur (like tab selection changes)
    const mutationObserver = new MutationObserver((mutations) => {
      const hasAriaSelectedChange = mutations.some(
        (mutation) => mutation.type === 'attributes' && mutation.attributeName === 'aria-selected'
      );
      if (hasAriaSelectedChange) {
        setTimeout(calculateOverflow, 0);
      }
    });

    // Find the tabs root for mutation observer
    const tabsRootForObserver = listRef.current?.closest('[data-orientation]');
    if (tabsRootForObserver) {
      mutationObserver.observe(tabsRootForObserver, {
        attributes: true,
        attributeFilter: ['aria-selected'],
        subtree: true,
      });
    }

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [tabItems, variant, resolvedVersion]);

  const handleTabSelect = useCallback(
    (value: string) => {
      const tabsRoot = listRef.current?.closest('[data-orientation]');
      const trigger = tabsRoot?.querySelector(
        `[role="tab"][data-value="${CSS.escape(value)}"]`
      ) as HTMLElement | null;

      if (trigger) {
        // Primary activation: programmatic click reaches Radix's handler.
        // onValueChange is called after so that any controlled state update it
        // triggers does not race with Radix's own click processing.
        trigger.click();

        // Notify the consumer after Radix has processed the click. This prop is
        // separate from Tabs.onValueChange (which Radix fires on the root); it
        // exists as an escape hatch for list-level callbacks.
        onValueChange?.(value);

        // Fallback activation: if click didn't activate (can happen with hidden
        // off-screen triggers), focus() activates via Radix's automatic mode.
        setTimeout(() => {
          if (trigger.getAttribute('aria-selected') !== 'true') {
            trigger.focus();
          }
        }, 10);

        // Signal onCloseAutoFocus to suppress Radix's focus-return to the overflow
        // button. Must be a ref (not state) so it's readable synchronously when
        // onCloseAutoFocus fires (which is deferred — state would already be stale).
        selectionMadeRef.current = true;

        // Defer the final focus until React has committed the trigger as visible.
        // At that point the trigger has its natural tabIndex (Radix roving tabindex
        // sets it to 0 for the active tab) and no aria-hidden override, so Shift+Tab
        // and arrow-key navigation work correctly after selection.
        setPendingFocusValue(value);
        return;
      }

      // Fallback when the trigger cannot be found in the DOM.
      onValueChange?.(value);
    },
    [onValueChange]
  );

  const overflowCount = overflowTabs.length;
  const overflowLabel =
    resolvedVersion === 'v1'
      ? `+${overflowCount} More`
      : overflowCount > 9
        ? '9+ more'
        : `${overflowCount} more`;

  return (
    <div ref={listRef} className={cn('tw:relative tw:flex tw:w-full tw:items-center')}>
      <TabsListPrimitive
        ref={ref}
        className={cn(
          tabsListVariants({ version: resolvedVersion, variant }),
          'tw:min-w-0 tw:flex-nowrap',
          className
        )}
        style={{
          // Apply minimum width in small viewports when there's overflow
          minWidth:
            overflowTabs.length > 0 && minWidthForActiveTab > 0
              ? `${minWidthForActiveTab}px`
              : undefined,
        }}
        {...props}
      >
        {/* Render tabs in original order, hiding overflow ones */}
        {tabItems.map((tab) => {
          const isVisible = visibleTabs.some((visibleTab) => visibleTab.value === tab.value);
          const isLastVisible =
            isVisible && visibleTabs[visibleTabs.length - 1]?.value === tab.value;
          const hasOverflow = overflowTabs.length > 0;
          const showCaret = isLastVisible && hasOverflow;

          return (
            <div
              key={tab.value}
              className={cn(
                'tw:relative tw:flex tw:h-auto tw:items-stretch tw:group',
                // Keep hidden tabs in the DOM for Radix UI (ARIA + keyboard management).
                // Position them far off-screen so they preserve their natural layout
                // dimensions — this is required for programmatic element.click() to work
                // (browsers skip activation on zero-size elements). overflow-x-clip is NOT
                // used on the list container so visible-tab focus rings are never clipped.
                // Do NOT use w-0/overflow-hidden (zero-size) or inert (Chrome 112+ blocks
                // programmatic clicks on both).
                !isVisible && 'tw:absolute tw:left-[-9999px] tw:opacity-0'
              )}
            >
              <TabsTrigger
                value={tab.value}
                version={resolvedVersion}
                variant={variant}
                icon={tab.icon}
                className={tab.className}
                asChild={tab.asChild}
                disabled={tab.disabled}
                // Prevent hidden tabs from being focusable
                {...(!isVisible && {
                  tabIndex: -1,
                  'aria-hidden': 'true',
                })}
              >
                {tab.asChild && isValidElement(tab.children)
                  ? cloneElement(tab.children, {
                      ...(!isVisible && {
                        tabIndex: -1,
                        'aria-hidden': 'true',
                      }),
                    })
                  : tab.children}
              </TabsTrigger>

              {showCaret && resolvedVersion === 'v0' && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      version="v0"

                      className={cn(
                        // Completely reset button defaults and force exact height match
                        'tw:h-auto tw:min-h-0 tw:border-0 tw:font-semibold tw:shadow-none',
                        // Match TabsTrigger exactly - copy all styles from tabsTriggerVariants
                        'tw:mr-[2px] tw:rounded-sm tw:px-[15px] tw:py-2.5 tw:text-sm',
                        // Colors and states - match TabsTrigger exactly (inactive state)
                        'tw:text-primary tw:hover:rounded-b-none tw:hover:bg-[#EEEEEE]',
                        'tw:data-[state=inactive]:text-primary',
                        // Open state - look hovered when dropdown is open
                        'tw:data-[state=open]:rounded-b-none tw:data-[state=open]:bg-[#EEEEEE]',
                        // Focus states - reset button defaults
                        'tw:focus-visible:border-0 tw:focus-visible:shadow-none tw:focus-visible:outline-hidden'
                      )}
                      onClick={(e) => e.stopPropagation()}
                      onMouseEnter={(e) => {
                        // Trigger hover on the tab
                        const tab = e.currentTarget
                          .closest('.tw\\:group')
                          ?.querySelector('[role="tab"]') as HTMLElement;
                        if (tab) {
                          tab.dispatchEvent(new MouseEvent('mouseenter', { bubbles: true }));
                        }
                      }}
                      onMouseLeave={(e) => {
                        // Trigger mouse leave on the tab
                        const tab = e.currentTarget
                          .closest('.tw\\:group')
                          ?.querySelector('[role="tab"]') as HTMLElement;
                        if (tab) {
                          tab.dispatchEvent(new MouseEvent('mouseleave', { bubbles: true }));
                        }
                      }}
                    >
                      <span className="tw:flex tw:items-center tw:gap-1">
                        {overflowLabel}
                        <FontAwesomeIcon
                          icon={faCaretDown}
                          aria-hidden="true"
                          className="tw:h-3 tw:w-3"
                        />
                      </span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="tw:min-w-[150px]">
                    {overflowTabs.map((overflowTab) => (
                      <DropdownMenuItem
                        key={overflowTab.value}
                        disabled={overflowTab.disabled}
                        onSelect={() => handleTabSelect(overflowTab.value)}
                        className="tw:flex tw:cursor-pointer tw:items-center tw:gap-2"
                      >
                        <div className="tw:pointer-events-none tw:flex tw:items-center tw:gap-2">
                          {overflowTab.icon && (
                            <FontAwesomeIcon
                              icon={overflowTab.icon}
                              aria-hidden="true"
                              className="tw:h-4 tw:w-4"
                            />
                          )}
                          {overflowTab.children}
                        </div>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          );
        })}
      </TabsListPrimitive>

      {/* v1 overflow trigger lives outside TabsListPrimitive so its focus ring
          is not clipped by the list. The button carries its own border-b (matching
          the strip color) so the strip line runs continuously under it, giving the
          focus ring the same "capped" bottom appearance as regular tabs. */}
      {resolvedVersion === 'v1' && overflowTabs.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              ref={overflowButtonRef}
              type="button"
              // Matches v1 TabsTrigger styling. Uses the same box-shadow technique as
              // active tabs to simulate the strip border below in the default state.
              // Focus shows a full 4-sided ring (no bottom cap needed — the overflow
              // button does not connect to a content panel the way an active tab does).
              // Open state erases the strip border so the dropdown appears connected.
              //   default: 0 1px 0 0 border-color  → simulates the strip border-b below
              //   focused: --shadow-state-focus     → full focus ring, all 4 sides
              //   open:    0 3px 0 0 surface-base   → erases strip border under dropdown
              className={cn(
                tabsTriggerV1,
                'tw:focus-visible:z-20 tw:focus-visible:border-(--color-border-dark) tw:focus-visible:border-b-0 tw:focus-visible:bg-(--color-surface-base) tw:focus-visible:shadow-(--shadow-state-focus) tw:data-[state=open]:z-20 tw:data-[state=open]:border-(--color-border-dark) tw:data-[state=open]:bg-(--color-surface-base) tw:data-[state=open]:shadow-[0_3px_0_0_var(--color-surface-base)]'
              )}
              aria-label={`Show ${overflowCount} more tab${overflowCount === 1 ? '' : 's'}`}
              onClick={(e) => e.stopPropagation()}
            >
              <span className="tw:flex tw:items-center tw:gap-(--spacing-4)">
                {overflowLabel}
                <FontAwesomeIcon icon={faCaretDown} aria-hidden="true" className="tw:h-3 tw:w-3" />
              </span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            className="tw:min-w-[150px]"
            onCloseAutoFocus={(e) => {
              // Suppress Radix's default focus-return only when a tab was just
              // selected — the pendingFocusValue effect moves focus to the
              // newly-visible trigger instead. When Escape dismisses the dropdown
              // without a selection, selectionMadeRef is false and Radix returns
              // focus to the overflow button (WCAG 2.1 SC 2.4.3).
              if (selectionMadeRef.current) {
                selectionMadeRef.current = false;
                e.preventDefault();
              }
            }}
          >
            {overflowTabs.map((overflowTab) => (
              <DropdownMenuItem
                key={overflowTab.value}
                disabled={overflowTab.disabled}
                onSelect={() => handleTabSelect(overflowTab.value)}
                className="tw:flex tw:cursor-pointer tw:items-center tw:gap-2"
              >
                <div className="tw:pointer-events-none tw:flex tw:items-center tw:gap-2">
                  {overflowTab.icon && (
                    <FontAwesomeIcon
                      icon={overflowTab.icon}
                      aria-hidden="true"
                      className="tw:h-4 tw:w-4"
                    />
                  )}
                  {overflowTab.children}
                </div>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Fills the remaining width to the right of the tabs/trigger with the
          border-b line. Split from TabsListPrimitive so no border sits directly
          below the overflow trigger button (which would clip its focus ring). */}
      {resolvedVersion === 'v1' && <div aria-hidden="true" className="tw:flex-1 tw:self-stretch" />}
    </div>
  );
});

TabsListWithOverflow.displayName = 'TabsListWithOverflow';

type TabsListProps = ComponentPropsWithoutRef<typeof TabsListPrimitive> &
  VariantProps<typeof tabsListVariants>;

const TabsList = forwardRef<ElementRef<typeof TabsListPrimitive>, TabsListProps>(
  ({ className, version, variant, ...props }, ref) => {
    const ctxVersion = useContext(TabsVersionContext);
    const resolvedVersion = version ?? ctxVersion;
    return (
      <TabsListPrimitive
        ref={ref}
        className={cn(tabsListVariants({ version: resolvedVersion, variant }), className)}
        {...props}
      />
    );
  }
);
TabsList.displayName = TabsListPrimitive.displayName;

const tabsContentV0Classic = [
  'tw:relative tw:z-10 tw:-mt-[1px]',
  'tw:border tw:border-(--color-border-dark) tw:border-b-0 tw:border-l-0 tw:border-r-0',
  'tw:px-2 tw:pb-4 tw:pt-2',
  // v0 focus ring (preserved byte-for-byte for legacy callers).
  'tw:ring-offset-background tw:focus-visible:outline-hidden',
  'tw:focus-visible:ring-2 tw:focus-visible:ring-ring tw:focus-visible:ring-offset-2',
].join(' ');

// v1 keeps a token-based top border so the panel "visually connects" to the strip
// (per upgrade interrogation Q3).
const tabsContentV1 = [
  'tw:relative tw:z-10',
  'tw:border tw:border-(--color-border-dark) tw:border-b-0 tw:border-l-0 tw:border-r-0',
  'tw:px-(--spacing-8) tw:pb-(--spacing-16) tw:pt-(--spacing-8)',
  'tw:focus-visible:outline-hidden tw:focus-visible:shadow-(--shadow-state-focus)',
].join(' ');

const tabsContentVariants = cva('', {
  variants: {
    version: { v0: tabsContentV0Classic, v1: tabsContentV1 },
    variant: { classic: '' },
  },
  defaultVariants: {
    version: 'v1',
  },
});

type TabsContentProps = ComponentPropsWithoutRef<typeof TabsContentPrimitive> &
  VariantProps<typeof tabsContentVariants>;

const TabsContent = forwardRef<ElementRef<typeof TabsContentPrimitive>, TabsContentProps>(
  ({ className, version, variant, ...props }, ref) => {
    const ctxVersion = useContext(TabsVersionContext);
    const resolvedVersion = version ?? ctxVersion;
    return (
      <TabsContentPrimitive
        ref={ref}
        className={cn(tabsContentVariants({ version: resolvedVersion, variant }), className)}
        {...props}
      />
    );
  }
);
TabsContent.displayName = TabsContentPrimitive.displayName;

export { Tabs, TabsContent, TabsList, TabsListWithOverflow, TabsTrigger };
export type { TabsRootProps, TabsListProps, TabsTriggerProps, TabsContentProps, TabsVersion };
